import { useState, useEffect } from 'react';

/**
 * Controls visibility of the StartPage overlay popup.
 *
 * GDPR compliance:
 * - Without Cookiebot consent: sessionStorage only (ephemeral, no tracking cookie).
 *   sessionStorage is not a cookie and does not require consent under GDPR/ePD.
 * - With Cookiebot consent: localStorage timestamp for 24-hour interval gating.
 *   localStorage is written ONLY after the user has actively accepted cookies.
 *
 * Result: popup is always shown to new visitors, not shown multiple times per
 * session for non-consenting users, and shown at most once per 24 hours for
 * consenting users.
 */

const LS_KEY = 'tco_startpage_overlay_last_shown';
const SS_KEY = 'tco_startpage_overlay_shown';
const INTERVAL_MS = 24 * 60 * 60 * 1000; // 24 hours

function resolveInitialVisibility() {
	if (typeof window === 'undefined') return false;

	try {
		const consented =
			typeof window.Cookiebot !== 'undefined' &&
			window.Cookiebot?.consent.necessary === true;

		if (consented) {
			const lastShown = localStorage.getItem(LS_KEY);
			if (
				lastShown &&
				Date.now() - parseInt(lastShown, 10) < INTERVAL_MS
			) {
				return false;
			}
		} else {
			if (sessionStorage.getItem(SS_KEY)) {
				return false;
			}
		}
	} catch {
		// Storage access denied (e.g. strict private mode) — default to showing.
	}

	return true;
}

export function useStartPageOverlay() {
	const [isVisible, setIsVisible] = useState(false);
	// Defer storage reads to client mount to prevent SSR hydration mismatch.
	useEffect(() => {
		setIsVisible(resolveInitialVisibility());
	}, []);

	const close = () => {
		try {
			const consented =
				typeof window.Cookiebot !== 'undefined' &&
				window.Cookiebot?.consent.necessary === true;

			if (consented) {
				localStorage.setItem(LS_KEY, Date.now().toString());
			} else {
				sessionStorage.setItem(SS_KEY, '1');
			}
		} catch {
			// Storage access denied — silently ignore, popup will reappear next visit.
		}
		setIsVisible(false);
	};

	return { isVisible, close };
}
